import EnhancedMenuItemDialogFormStateless, {
  MenuItemFormData,
} from "@/components/EnhancedMenuItemDialogForm.stateless";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
  ItemDescription,
} from "@/components/ui/item";
import { useCurrentRestaurant } from "@/hooks/useCurrentRestaurant";
import { MenuItem } from "@/model/entities";
import { useCategories } from "@/query/useCategories";
import { useMenuItemsByRestaurant } from "@/query/useMenuItems";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { usePlan } from "@/hooks/usePlan";
import ButtonWithTooltip from "@/components/ButtonWithTooltip";
import { getMaxOrderInCategory } from "@/utils/menuItemsUtils";
import MenuItemsView from "./menuItems/MenuItemsView";

const MenuManagementPage2 = () => {
  const { t } = useTranslation();
  const [showMenuItemDialog, setShowMenuItemDialog] = useState(false);
  const [menuItemToEdit, setMenuItemToEdit] = useState<MenuItem | null>(null);

  const {
    restaurantId,
    restaurant,
    isLoading: isLoadingRestaurant,
  } = useCurrentRestaurant();

  const { isAllowedToAddMenuItem } = usePlan();

  const {
    data: menuItems,
    isLoading: isLoadingMenuItems,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    updateBulkMenuItems,
  } = useMenuItemsByRestaurant(restaurantId);
  const { data: categories, isLoading: isLoadingCategories } =
    useCategories(restaurantId);

  const createMenuItem = async (item: MenuItemFormData) => {
    try {
      const maxOrder = getMaxOrderInCategory(menuItems || [], item.categoryId);
      item = { ...item, order: maxOrder + 1 };
      console.log("Creating menu item", item);
      await addMenuItem.mutateAsync(item);
      toast.success(t("menuItem.createdSuccess", "הפריט נוצר בהצלחה"));
      handleCloseMenuItemDialog();
    } catch (err) {
      console.error("Error creating menu item", err);
      toast.error(t("menuItem.createdError", "שגיאה ביצירת פריט"));
    }
  };

  const handleCloseMenuItemDialog = () => {
    setShowMenuItemDialog(false);
    setMenuItemToEdit(null);
  };

  const onEditMenuItem = (item: MenuItem) => {
    setMenuItemToEdit(item);
    setShowMenuItemDialog(true);
  };

  const handleUpdateMenuItem = async (id: string, item: MenuItemFormData) => {
    if (!id) return;
    try {
      // Ensure isAvailable is included in updatedItem
      await updateMenuItem.mutateAsync({ id, updatedItem: item });
      toast.success(t("menuItem.updatedSuccess", "הפריט עודכן בהצלחה"));
      handleCloseMenuItemDialog();
    } catch (err) {
      console.error("Error updating menu item", err);
      toast.error(t("menuItem.updatedError", "שגיאה בעדכון פריט"));
    }
  };

  const onDeleteMenuItem = async (item: MenuItem) => {
    try {
      await deleteMenuItem.mutateAsync(item.id!);
      toast.success(t("menuItem.deletedSuccess", "הפריט נמחק בהצלחה"));
    } catch (err) {
      console.error("Error deleting menu item", err);
      toast.error(t("menuItem.deletedError", "שגיאה במחיקת פריט"));
    }
  };

  const onReorderMenuItems = async (items: MenuItem[]) => {
    console.log("Reordering items", items);
    try {
      await updateBulkMenuItems.mutateAsync(items);
      toast.success(t("menuItem.reorderSuccess", "הפריטים שונו בהצלחה"));
    } catch (error) {
      console.error("Error reordering menu items", error);
      toast.error(t("menuItem.reorderError", "שגיאה בשינוי סדר הפריטים"));
    }
  };

  const isLoading =
    isLoadingRestaurant || isLoadingMenuItems || isLoadingCategories;

  const hasCategories = (categories || []).length > 0;
  const canAddMenuItem = isAllowedToAddMenuItem && hasCategories;

  let disabledTooltip = "";
  if (!isAllowedToAddMenuItem) {
    disabledTooltip = t(
      "plan.upgradeToAddMoreItemsTooltip",
      "שדרג את התוכנית שלך כדי להוסיף פריטים נוספים"
    );
  } else if (!hasCategories) {
    disabledTooltip = t(
      "menuItem.noCategoriesForItemsTooltip",
      "עליך להוסיף קטגוריה לפני הוספת פריטים"
    );
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="container mx-auto">
        <Item>
          <ItemContent>
            <ItemTitle className="text-2xl font-bold mb-2">
              {restaurant?.restaurantName}
            </ItemTitle>
            <ItemDescription>
              {t("manageMenu.description", "ניהול התפריט של המסעדה שלך")}
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <ButtonWithTooltip
              className="gap-2 bg-orange-500 hover:bg-orange-600"
              disabled={!canAddMenuItem}
              disabledTooltip={disabledTooltip}
              onClick={() => setShowMenuItemDialog(true)}
            >
              <Plus className="w-4 h-4" />
              {t("addItem")}
            </ButtonWithTooltip>
          </ItemActions>
        </Item>
      </div>

      <MenuItemsView
        menuItems={menuItems || []}
        categories={categories || []}
        onEditMenuItem={onEditMenuItem}
        onDeleteMenuItem={onDeleteMenuItem}
        onReorderMenuItems={onReorderMenuItems}
      />

      <EnhancedMenuItemDialogFormStateless
        open={showMenuItemDialog}
        mode={menuItemToEdit ? "edit" : "add"}
        defaultValues={menuItemToEdit || undefined}
        categories={categories || []}
        onAdd={createMenuItem}
        onUpdate={handleUpdateMenuItem}
        onCancel={handleCloseMenuItemDialog}
      />
    </>
  );
};

export default MenuManagementPage2;
